import React, { useState } from 'react'
import { Button, Card, Grid, makeStyles, Typography } from '@material-ui/core'
import { Form, Formik } from 'formik'
import { sponsorFormSchema } from 'schemas/sponsor'
import CustomTextField from 'components/Form/CustomTextField'
import CustomSelect from 'components/Form/CustomSelect'
import { Collection, SPONSOR_INFO_TEXT, SubjectInfoMessages, subjectReason } from 'constants/index'
import { Box } from '@mui/material'
import ProgressButton from 'components/ProgressButton'
import { useDispatch, useSelector } from 'react-redux'
import { resetSubject, submitSubjectData, verifyGoogleRecaptcha } from 'store/subject'
import { toast } from 'react-toastify'
import Recaptcha from 'components/Recaptcha'
import { useHistory } from 'react-router-dom'
import SubjectSpecificDetails from 'components/SubjectSpecificDetails'
import AvatarPicker from 'components/AvatarPicker'
import { updateSponsorImage } from 'store/sponsor'
import InfoIconAlongText from 'components/InfoIconAlongText'
import ReactQuill from 'react-quill'
import { modules } from 'views/admin/settings/SettingForm/TemplateForm'
import ErrorMessage from 'components/ErrorMessage'

const SponsorImageKeys = {
  logoImage: 'logoImageName',
  adImage: 'adImageName',
  brochureImage: 'brochureImageName'
}

const Sponsor = (props) => {
  const { subject, submitTitle, showSponsorshipLevel, sponsorshipLevels, handleSubmit, reason } =
    props
  const dispatch = useDispatch()
  const history = useHistory()
  const classes = useStyles()
  const [loading, setLoading] = useState(false)
  const [recaptchaCode, setRecaptchaCode] = useState('')
  const [isRecaptchaValid, setIsRecaptchaValid] = useState(false)

  const { conference } = useSelector((state) => state.conference)

  const handleChangeRecaptcha = (value) => {
    setRecaptchaCode(value)
    if (value) {
      setIsRecaptchaValid(true)
    }
  }
  const handleExpired = () => {
    setIsRecaptchaValid(false)
  }

  const initialValues = {
    email: subject?.email ?? '',
    fullName: subject?.fullName ?? '',
    intro: subject?.intro ?? '',
    websiteUrl: subject?.websiteUrl ?? '',
    sponsorMessage: subject?.sponsorMessage ?? '',
    sponsorshipLevelId: subject?.sponsorshipLevelId ?? '',
    sponsorAdTargetURL: subject?.sponsorAdTargetURL ?? '',
    booth: subject?.booth ?? ''
  }

  const goBack = () => {
    dispatch(resetSubject())
    history.goBack()
  }

  const handleUpload = (key) => async (name) => {
    const modSubject = { ...subject }
    const imagePayload = {}
    if (key === SponsorImageKeys.logoImage) {
      modSubject[SponsorImageKeys.logoImage] = name
      imagePayload[SponsorImageKeys.logoImage] = name
    } else if (key === SponsorImageKeys.adImage) {
      modSubject[SponsorImageKeys.adImage] = name
      imagePayload[SponsorImageKeys.adImage] = name
    } else {
      modSubject[SponsorImageKeys.brochureImage] = name
      imagePayload[SponsorImageKeys.brochureImage] = name
    }
    await dispatch(submitSubjectData(subject?.id, modSubject))
    reason === subjectReason.afterApproval &&
      (await updateSponsorImage(subject?.sponsorId, imagePayload))
  }

  const handleDelete = (key) => async () => {
    const modSubject = { ...subject }
    const imagePayload = {}
    if (key === SponsorImageKeys.logoImage) {
      modSubject[SponsorImageKeys.logoImage] = null
      imagePayload[SponsorImageKeys.logoImage] = null
    } else if (key === SponsorImageKeys.adImage) {
      modSubject[SponsorImageKeys.adImage] = null
      imagePayload[SponsorImageKeys.adImage] = null
    } else {
      modSubject[SponsorImageKeys.brochureImage] = null
      imagePayload[SponsorImageKeys.brochureImage] = null
    }
    await dispatch(submitSubjectData(subject?.id, modSubject))
    reason === subjectReason.afterApproval &&
      (await updateSponsorImage(subject?.sponsorId, imagePayload))
  }

  return (
    <Card className={reason === subjectReason.registration ? classes.card : ''} elevation={0}>
      <Formik
        initialValues={initialValues}
        enableReinitialize={true}
        validationSchema={sponsorFormSchema({ showSponsorshipLevel: showSponsorshipLevel })}
        onSubmit={async (values) => {
          try {
            setLoading(true)
            if (reason === subjectReason.registration) {
              const isVerified = await dispatch(verifyGoogleRecaptcha(recaptchaCode))
              if (isVerified) {
                await handleSubmit(values)
              } else {
                toast.error('reCAPTCHA verification is failed.')
              }
            } else {
              await handleSubmit(values, reason)
            }
            setLoading(false)
          } catch (e) {
            // do nothing
          } finally {
            setLoading(false) // do nothing but stops the loading
          }
        }}>
        {({ setFieldValue, errors, values, touched }) => (
          <Form>
            <Box className={classes.headBar}>
              <Typography variant="h4">
                {reason === subjectReason.forApproval
                  ? 'Review Sponsor'
                  : reason === subjectReason.afterApproval
                  ? 'Edit Sponsor'
                  : 'Register as a Sponsor'}
              </Typography>
              {reason !== subjectReason.registration && (
                <Box sx={{ display: 'flex', gap: '1rem' }}>
                  <Button size="small" color="primary" variant="contained" onClick={goBack}>
                    back
                  </Button>
                  <ProgressButton
                    type="submit"
                    label={submitTitle}
                    inProgress={loading}
                    disabled={loading}
                  />
                </Box>
              )}
            </Box>

            {reason !== subjectReason.registration && (
              <Box p={1} border={1} sx={{ mb: '2rem' }}>
                <Box
                  p={1}
                  sx={{
                    backgroundColor: '#ECECEC',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.5rem'
                  }}>
                  <Box className={classes.singleInfo}>
                    <Typography className={classes.label}>Conference Name:</Typography>
                    <Typography>{conference?.confName}</Typography>
                  </Box>
                </Box>
              </Box>
            )}

            {reason === subjectReason.registration && (
              <SubjectSpecificDetails infoText={SubjectInfoMessages.SPONSOR} />
            )}

            <Grid container spacing={3} className={classes.form}>
              <Grid item xs={12} lg={4}>
                <CustomTextField
                  label="Email"
                  name="email"
                  type="email"
                  disabled={reason === subjectReason.registration}
                />
              </Grid>
              <Grid item xs={12} lg={4}>
                <CustomTextField label="Company" name="fullName" type="text" />
              </Grid>
              <Grid item xs={12} md={4}>
                <CustomTextField label="Booth No" name="booth" type="text" />
              </Grid>

              {showSponsorshipLevel && (
                <Grid item xs={12} lg={4}>
                  <CustomSelect
                    inputLabel="Sponsorship Level"
                    options={sponsorshipLevels}
                    optionAttr="category"
                    name="sponsorshipLevelId"
                  />
                </Grid>
              )}
              <Grid item xs={12} lg={showSponsorshipLevel ? 8 : 6}>
                <CustomTextField label="Website URL" name="websiteUrl" type="text" />
              </Grid>
              <Grid item xs={6} lg={showSponsorshipLevel ? 12 : 6}>
                <CustomTextField
                  label="Banner Click URL"
                  name="sponsorAdTargetURL"
                  type="text"
                  infoText={SPONSOR_INFO_TEXT.bannerClickURL}
                />
              </Grid>
              <Grid item xs={12} lg={12}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  <label className={classes.labelA}>
                    Introduction
                    <InfoIconAlongText infoText={SPONSOR_INFO_TEXT.introduction} />
                  </label>
                  <ReactQuill
                    theme="snow"
                    modules={modules}
                    value={values.intro}
                    onChange={(value) => setFieldValue('intro', value)}
                    placeholder="Write introduction here..."
                  />
                  <ErrorMessage message={touched?.intro && errors?.intro} />
                </Box>
              </Grid>
              <Grid item xs={12} lg={12}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  <label className={classes.labelA}>
                    Message
                    <InfoIconAlongText infoText={SPONSOR_INFO_TEXT.message} />
                  </label>
                  <ReactQuill
                    theme="snow"
                    modules={modules}
                    value={values.sponsorMessage}
                    onChange={(value) => setFieldValue('sponsorMessage', value)}
                    placeholder="Write message here..."
                  />
                  <ErrorMessage message={touched?.sponsorMessage && errors?.sponsorMessage} />
                </Box>
              </Grid>

              <Grid item xs={12} lg={6}>
                <AvatarPicker
                  title="Sponsor Logo"
                  subTitle="Allowed Format: png/jpg ; Dimensions : 320x320"
                  path={Collection.SPONSORS}
                  onUpload={(name) => handleUpload(SponsorImageKeys.logoImage)(name)}
                  onDelete={() => handleDelete(SponsorImageKeys.logoImage)()}
                  imageName={subject?.logoImageName ? subject?.logoImageName : null}
                  resetImage={!subject?.logoImageName}
                  isSubject={true}
                  conferenceId={subject?.conferenceId}
                  infoText={SPONSOR_INFO_TEXT.logo}
                  reason={reason}
                />
              </Grid>
              <Grid item xs={12} lg={6}>
                <AvatarPicker
                  title="Sponsor Ad Banner"
                  subTitle="Allowed Format: png/jpg ; Dimensions : 320x50"
                  path={Collection.SPONSORS}
                  onUpload={(name) => handleUpload(SponsorImageKeys.adImage)(name)}
                  onDelete={() => handleDelete(SponsorImageKeys.adImage)()}
                  imageName={subject?.adImageName ? subject?.adImageName : null}
                  resetImage={!subject?.adImageName}
                  isSubject={true}
                  conferenceId={subject?.conferenceId}
                  infoText={SPONSOR_INFO_TEXT.adBanner}
                  reason={reason}
                />
              </Grid>

              <Grid item xs={12} lg={6}>
                <AvatarPicker
                  title="Sponsor Brochure"
                  subTitle="Allowed Format: png/jpg ; Dimensions : 320x50"
                  path={Collection.SPONSORS}
                  onUpload={(name) => handleUpload(SponsorImageKeys.brochureImage)(name)}
                  onDelete={() => handleDelete(SponsorImageKeys.brochureImage)()}
                  imageName={subject?.brochureImageName ? subject?.brochureImageName : null}
                  resetImage={!subject?.brochureImageName}
                  isSubject={true}
                  conferenceId={subject?.conferenceId}
                  infoText={SPONSOR_INFO_TEXT.brochure}
                  reason={reason}
                />
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Box className={classes.buttonBox}>
                {reason === subjectReason.registration && (
                  <Recaptcha handleChange={handleChangeRecaptcha} onExpired={handleExpired} />
                )}
                <Box sx={{ display: 'flex', gap: '1rem' }}>
                  {reason !== subjectReason.registration && (
                    <Button onClick={goBack}>Cancel</Button>
                  )}
                  <ProgressButton
                    type="submit"
                    label={submitTitle}
                    inProgress={loading}
                    disabled={
                      loading || (reason === subjectReason.registration && !isRecaptchaValid)
                    }
                  />
                </Box>
              </Box>
            </Grid>
          </Form>
        )}
      </Formik>
    </Card>
  )
}

export default Sponsor

const useStyles = makeStyles({
  labelA: {
    fontSize: '13px',
    letterSpacing: '0.5px',
    color: '#606060',
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
    marginLeft: '0.2rem'
  },
  headBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    margin: '0rem 0rem 2rem 0rem'
  },
  card: {
    padding: '1rem',
    border: '1px solid #e0e0e0'
  },
  header: {
    fontWeight: 'bold'
  },
  form: {
    marginTop: '2rem'
  },
  singleInfo: {
    display: 'flex',
    gap: '2rem'
  },
  label: {
    minWidth: '132px',
    fontSize: '15px',
    fontWeight: '600'
  },
  infoBox: {
    padding: '10px 16px',
    borderRadius: '8px',
    backgroundColor: '#e0f7fa'
  },
  buttonBox: {
    marginTop: '1rem',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '1rem'
  }
})
